import apiClient from './client';
import authService from '../services/authService';
import axios from 'axios';

// Get the app ID from environment
const APP_ID = process.env.REACT_APP_APP_ID || '1';

export interface UserData {
  id: string | number;
  username?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  name?: string; // For backward compatibility
  bio?: string;
  avatar_url?: string;
  date_of_birth?: string;
  phone_number?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  website?: string;
  company?: string;
  job_title?: string;
  is_verified?: boolean;
  is_active?: boolean;
  role?: string;
  roles?: string[];
  created_at?: string;
  updated_at?: string;
}

export interface UserUpdateData {
  first_name?: string;
  last_name?: string;
  display_name?: string;
  bio?: string;
  avatar_url?: string;
  phone_number?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  postal_code?: string;
  website?: string;
  company?: string;
  job_title?: string;
}

export interface UserCreateData {
  email: string;
  password: string;
  name: string;
  role: string;
}

export interface UsersResponse {
  success: boolean;
  data: UserData[];
}

export const userService = {
  getCurrentUser: async () => {
    // First get the user profile
    const userResponse = await apiClient.get<{message?: string, user: UserData}>('/users/me');
    
    // Log response to help debug
    console.log('User profile response:', userResponse.data);
    
    try {
      // Then get the user roles using the correct endpoint
      const rolesResponse = await apiClient.get('/users/roles');
      console.log('Roles response:', rolesResponse.data);
      
      // Extract roles from the response based on API documentation format
      const roles = rolesResponse.data?.roles?.map((role: any) => {
        // Handle both string and object formats
        if (typeof role === 'string') return role;
        return role.name;
      }) || [];
      
      console.log('Extracted roles:', roles);
      
      // Get user from the response, handling different API response formats
      const user = userResponse.data.user || userResponse.data;
      
      // Combine the user data with roles
      const userData = {
        user: {
          ...user,
          roles
        }
      };
      
      console.log('Final user data:', userData);
      return userData;
    } catch (error) {
      console.error('Error fetching roles:', error);
      // Return user data even if roles fetch fails, handling different response formats
      return { user: userResponse.data.user || userResponse.data };
    }
  },
  
  updateProfile: (userData: UserUpdateData) => 
    apiClient.put<{message: string, user: UserData}>('/users/profile', userData),
  
  getUsers: () => 
    apiClient.get<UsersResponse>(`/apps/${APP_ID}/users`, {
      params: {
        timestamp: Date.now() // Simple cache-busting parameter
      }
    }),
  
  getUserById: (id: string | number) => 
    apiClient.get<{user: UserData}>(`/users/${id}`),
  
  updateCredentials: (data: {
    currentPassword: string;
    username?: string;
    email?: string;
    newPassword?: string;
  }) => 
    apiClient.put<{user: UserData}>('/users/credentials', data),
    
  createUser: async (userData: UserCreateData) => {
    try {
      // Log the request payload
      console.log('Registering user with data:', userData);
      
      // Register the user via direct API call
      const registerResponse = await apiClient.post<{message: string, user: UserData, tokens?: any}>('/auth/register', {
        username: userData.email.split('@')[0],
        email: userData.email,
        password: userData.password,
        first_name: userData.name.split(' ')[0],
        last_name: userData.name.split(' ').slice(1).join(' '),
        display_name: userData.name
      });
      
      const userId = registerResponse.data?.user?.id;
      const userToken = registerResponse.data?.tokens?.accessToken;
      
      if (!userId || !userToken) {
        throw new Error('User registration did not return required data');
      }

      // Create a new axios instance with the new user's token
      const userApiClient = axios.create({
        baseURL: apiClient.defaults.baseURL,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${userToken}`
        }
      });

      // Associate the user with the app using the self-service endpoint
      await userApiClient.post(`/apps/${APP_ID}/join`);
      
      console.log('User registered and associated with app');
      return registerResponse;
    } catch (error: any) {
      console.error('Full error response:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        payload: userData
      });
      throw error;
    }
  },
    
  resetPassword: (userId: string | number, newPassword: string) =>
    apiClient.post<{message: string}>(`/apps/${APP_ID}/users/${userId}/reset-password`, { password: newPassword }),
    
  deleteUser: (userId: string | number) =>
    apiClient.delete<{message: string}>(`/apps/${APP_ID}/users/${userId}`),
}; 